import React, { useState } from 'react';

/* ================================================================
   Grouped, collapsible sidebar panel for map layer controls.
   Groups: Traffic, Competition, Zones, Overlays
   ================================================================ */

const GROUPS = [
  {
    id: 'traffic',
    title: 'Traffic',
    icon: '🚦',
    items: [
      { key: 'trafficSensors', icon: '🚦', label: 'Traffic Sensors' },
      { key: 'sensorLines', icon: '📡', label: 'Sensor Lines' },
    ],
  },
  {
    id: 'competition',
    title: 'Competition',
    icon: '⛽',
    items: [
      { key: 'gasStations', icon: '⛽', label: 'Gas Stations' },
      { key: 'cStores', icon: '🏪', label: 'C-Stores' },
      { key: 'heatmap', icon: '🔥', label: 'Competitor Heatmap' },
    ],
  },
  {
    id: 'zones',
    title: 'Zones',
    icon: '⭕',
    items: [
      { key: 'alcoholZones', icon: '⚠️', label: 'Alcohol Zones' },
      { key: 'radiusRings', icon: '⭕', label: 'Radius Rings' },
    ],
  },
  {
    id: 'overlays',
    title: 'Overlays',
    icon: '🌙',
    items: [
      { key: 'nightLights', icon: '🌙', label: 'Night Lights', hasOpacity: true },
      { key: 'propertyCard', icon: '🏠', label: 'Property Info' },
    ],
  },
];

const LayerTogglePanel = ({ layers, onChange, nightLightsOpacity, onOpacityChange }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggle = (key) => {
    onChange({ ...layers, [key]: !layers[key] });
  };

  const toggleGroup = (groupId) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const activeCount = Object.values(layers).filter(Boolean).length;

  return (
    <div style={{
      position: 'absolute',
      top: 12,
      right: 12,
      background: 'rgba(15, 23, 42, 0.92)',
      backdropFilter: 'blur(12px)',
      borderRadius: 14,
      color: '#e2e8f0',
      fontSize: 13,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      zIndex: 10,
      boxShadow: '0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
      minWidth: collapsed ? 48 : 220,
      maxHeight: 'calc(100vh - 300px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      transition: 'min-width 0.25s ease, padding 0.25s ease',
      userSelect: 'none',
    }}>
      {/* Header */}
      <div
        onClick={() => setCollapsed(!collapsed)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: collapsed ? '10px 14px' : '12px 16px',
          cursor: 'pointer',
          borderBottom: collapsed ? 'none' : '1px solid rgba(255,255,255,0.08)',
        }}
      >
        {!collapsed && (
          <span style={{ fontWeight: 700, fontSize: 14, letterSpacing: '0.01em' }}>
            Map Layers
          </span>
        )}
        <span style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 12, color: '#94a3b8',
        }}>
          {!collapsed && <span style={{
            background: '#3b82f6', color: '#fff', borderRadius: 10,
            padding: '1px 7px', fontSize: 11, fontWeight: 600,
          }}>{activeCount}</span>}
          <span style={{ fontSize: 16, lineHeight: 1 }}>{collapsed ? '◀' : '▶'}</span>
        </span>
      </div>

      {/* Groups */}
      {!collapsed && GROUPS.map((group) => {
        const isGroupCollapsed = collapsedGroups[group.id];
        const groupActiveCount = group.items.filter((i) => layers[i.key]).length;

        return (
          <div key={group.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            {/* Group header */}
            <div
              onClick={() => toggleGroup(group.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              <span style={{
                fontWeight: 600, fontSize: 12, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.06em',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ fontSize: 13 }}>{group.icon}</span>
                {group.title}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {groupActiveCount > 0 && (
                  <span style={{
                    background: 'rgba(59,130,246,0.25)', color: '#60a5fa',
                    borderRadius: 8, padding: '0 6px', fontSize: 10, fontWeight: 600,
                  }}>
                    {groupActiveCount}
                  </span>
                )}
                <span style={{ fontSize: 10, color: '#64748b', transition: 'transform 0.2s', transform: isGroupCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>
                  ▼
                </span>
              </span>
            </div>

            {/* Group items */}
            {!isGroupCollapsed && (
              <div style={{ padding: '4px 0' }}>
                {group.items.map(({ key, icon, label, hasOpacity }) => (
                  <div key={key}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '5px 16px 5px 24px',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: 4,
                        border: layers[key] ? '2px solid #3b82f6' : '2px solid #475569',
                        background: layers[key] ? '#3b82f6' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s ease',
                        flexShrink: 0,
                      }}>
                        {layers[key] && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={!!layers[key]}
                        onChange={() => toggle(key)}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: 13 }}>{icon} {label}</span>
                    </label>

                    {/* Opacity slider for Night Lights */}
                    {hasOpacity && layers[key] && (
                      <div style={{
                        padding: '4px 24px 8px 44px',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>Opacity</span>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={nightLightsOpacity ?? 0.6}
                          onChange={(e) => onOpacityChange?.(parseFloat(e.target.value))}
                          style={{
                            flex: 1,
                            height: 4,
                            accentColor: '#8b5cf6',
                            cursor: 'pointer',
                          }}
                        />
                        <span style={{ fontSize: 11, color: '#94a3b8', minWidth: 28, textAlign: 'right' }}>
                          {Math.round((nightLightsOpacity ?? 0.6) * 100)}%
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LayerTogglePanel;
